// B2B Leads Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, formatTimeAgo, getInitials, getScoreClass } from '../utils/api';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'booked', label: 'Booked' },
  { key: 'lost', label: 'Lost' }
];

const DATE_RANGES = [
  { key: 'all', label: 'All Time' },
  { key: '7days', label: 'Last 7 Days' },
  { key: '30days', label: 'Last 30 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'custom', label: 'Custom Range' }
];

export default function B2BLeads() {
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStatus, setExportStatus] = useState('all'); // all, filtered
  const [exportDateRange, setExportDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadLeads();
  }, [filter]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      
      const data = await getLeads(params);
      setLeads(data.leads || []);
      
      // Calculate counts per status
      if (filter === 'all') {
        const allLeads = data.leads || [];
        const newCounts = {
          all: allLeads.length,
          new: allLeads.filter(l => l.status === 'new').length,
          contacted: allLeads.filter(l => l.status === 'contacted').length,
          qualified: allLeads.filter(l => l.status === 'qualified').length,
          booked: allLeads.filter(l => l.status === 'booked').length,
          lost: allLeads.filter(l => l.status === 'lost').length
        };
        setCounts(newCounts);
      }
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filteredLeads = leads.filter(lead => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      lead.client_name?.toLowerCase().includes(searchLower) ||
      lead.client_email?.toLowerCase().includes(searchLower) ||
      lead.trip_summary?.destination?.toLowerCase().includes(searchLower)
    );
  });

  // ─────────────────────────────────────────────────────────────────
  // EXPORT FUNCTIONS
  // ─────────────────────────────────────────────────────────────────
  
  const getDateRangeParams = () => {
    const now = new Date();
    let startDate = null;
    let endDate = null;
    
    switch (exportDateRange) {
      case '7days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        if (customStartDate) startDate = new Date(customStartDate);
        if (customEndDate) endDate = new Date(customEndDate);
        break;
      default:
        // 'all' - no date filter
        break;
    }
    
    return { startDate, endDate };
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      // Build query params
      const params = new URLSearchParams();
      
      // Status filter
      if (exportStatus === 'filtered' && filter !== 'all') {
        params.append('status', filter);
      }
      
      // Date range filter
      const { startDate, endDate } = getDateRangeParams();
      if (startDate) {
        params.append('startDate', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString().split('T')[0]);
      }
      
      // Get auth token
      const token = localStorage.getItem('b2b_token');
      
      // Fetch CSV
      const response = await fetch(`/api/b2b/leads/export?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the CSV content
      const csv = await response.text();
      
      // Create download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const statusSuffix = exportStatus === 'filtered' && filter !== 'all' ? `-${filter}` : '';
      const filename = `leads-export${statusSuffix}-${dateStr}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close modal
      setShowExportModal(false);
      
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export leads. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Calculate export count preview
  const getExportCount = () => {
    if (exportStatus === 'filtered') {
      return filteredLeads.length;
    }
    return counts.all || 0;
  };

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">Leads</h1>
          <p className="b2b-page-subtitle">
            {counts.all || 0} total leads • {counts.new || 0} new this month
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            className="b2b-form-input"
            placeholder="🔍 Search leads..."
            style={{ width: '250px' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button 
            className="b2b-btn b2b-btn-secondary"
            onClick={() => setShowExportModal(true)}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Leads Panel */}
      <div className="b2b-panel">
        {/* Filters */}
        <div className="b2b-leads-filters">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`b2b-filter-btn ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} {counts[f.key] !== undefined && `(${counts[f.key]})`}
            </button>
          ))}
        </div>

        {/* Leads List */}
        {loading ? (
          <div className="b2b-loading">
            <div className="b2b-spinner" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="b2b-empty-state">
            <div className="b2b-empty-state-icon">👥</div>
            <div className="b2b-empty-state-title">
              {search ? 'No matching leads' : 'No leads yet'}
            </div>
            <div className="b2b-empty-state-desc">
              {search 
                ? 'Try a different search term' 
                : 'Install the widget on your website to start capturing leads'}
            </div>
          </div>
        ) : (
          <div className="b2b-leads-list" style={{ maxHeight: 'none' }}>
            {filteredLeads.map(lead => (
              <div
                key={lead.id}
                className={`b2b-lead-item ${lead.status === 'new' ? 'unread' : ''}`}
                onClick={() => navigate(`/leads/${lead.id}`)}
              >
                {/* Score */}
                <div className={`b2b-lead-score ${getScoreClass(lead.score || 0)}`}>
                  {lead.score || 0}
                </div>
                
                {/* Avatar */}
                <div className="b2b-lead-avatar">
                  {getInitials(lead.client_name)}
                </div>
                
                {/* Info */}
                <div className="b2b-lead-info">
                  <div className="b2b-lead-name">{lead.client_name || 'Anonymous'}</div>
                  <div className="b2b-lead-destination">
                    {lead.client_email || 'No email'} {lead.client_phone && `• ${lead.client_phone}`}
                  </div>
                </div>
                
                {/* Trip Details */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#18181b' }}>
                    {typeof lead.trip_summary?.destination === 'string' 
                      ? lead.trip_summary.destination 
                      : 'No destination'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>
                    {typeof lead.trip_summary?.travel_dates === 'string' 
                      ? lead.trip_summary.travel_dates 
                      : lead.trip_summary?.travel_dates?.start 
                        ? `${lead.trip_summary.travel_dates.start}${lead.trip_summary.travel_dates.end ? ' - ' + lead.trip_summary.travel_dates.end : ''}`
                        : 'No dates'} 
                    {lead.trip_summary?.travelers && ` • ${lead.trip_summary.travelers}`}
                  </div>
                </div>
                
                {/* Meta */}
                <div className="b2b-lead-meta">
                  <div className="b2b-lead-time">{formatTimeAgo(lead.created_at)}</div>
                  <div className={`b2b-lead-status ${lead.status}`}>
                    {lead.status === 'new' ? 'New' : 
                     lead.status === 'contacted' ? 'Contacted' :
                     lead.status === 'qualified' ? 'Qualified' :
                     lead.status === 'booked' ? 'Booked!' : 
                     lead.status === 'lost' ? 'Lost' : lead.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EXPORT MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showExportModal && (
        <div className="b2b-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="b2b-modal" onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h2 className="b2b-modal-title">📥 Export Leads to CSV</h2>
              <button 
                className="b2b-modal-close"
                onClick={() => setShowExportModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="b2b-modal-body">
              {/* Status Filter */}
              <div className="b2b-form-group">
                <label className="b2b-form-label">Which leads to export?</label>
                <div className="b2b-radio-group">
                  <label className="b2b-radio-label">
                    <input
                      type="radio"
                      name="exportStatus"
                      value="all"
                      checked={exportStatus === 'all'}
                      onChange={() => setExportStatus('all')}
                    />
                    <span>All leads ({counts.all || 0})</span>
                  </label>
                  <label className="b2b-radio-label">
                    <input
                      type="radio"
                      name="exportStatus"
                      value="filtered"
                      checked={exportStatus === 'filtered'}
                      onChange={() => setExportStatus('filtered')}
                    />
                    <span>
                      Current filter: {FILTERS.find(f => f.key === filter)?.label} 
                      ({filter === 'all' ? counts.all : counts[filter] || 0})
                    </span>
                  </label>
                </div>
              </div>

              {/* Date Range */}
              <div className="b2b-form-group">
                <label className="b2b-form-label">Date range</label>
                <select
                  className="b2b-form-input"
                  value={exportDateRange}
                  onChange={(e) => setExportDateRange(e.target.value)}
                >
                  {DATE_RANGES.map(range => (
                    <option key={range.key} value={range.key}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Date Range */}
              {exportDateRange === 'custom' && (
                <div className="b2b-form-row">
                  <div className="b2b-form-group" style={{ flex: 1 }}>
                    <label className="b2b-form-label">Start Date</label>
                    <input
                      type="date"
                      className="b2b-form-input"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div className="b2b-form-group" style={{ flex: 1 }}>
                    <label className="b2b-form-label">End Date</label>
                    <input
                      type="date"
                      className="b2b-form-input"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Export Preview */}
              <div className="b2b-export-preview">
                <div className="b2b-export-preview-icon">📊</div>
                <div className="b2b-export-preview-text">
                  Your CSV will include these columns:
                  <br />
                  <small style={{ color: '#71717a' }}>
                    Name, Email, Phone, Destination, Travel Dates, Travelers, Budget, Trip Type, Status, Score, Created Date, Source URL
                  </small>
                </div>
              </div>
            </div>
            
            <div className="b2b-modal-footer">
              <button 
                className="b2b-btn b2b-btn-secondary"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button 
                className="b2b-btn b2b-btn-primary"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="b2b-spinner-small" /> Exporting...
                  </>
                ) : (
                  <>📥 Download CSV</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EXPORT MODAL STYLES (inline for simplicity) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <style>{`
        .b2b-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .b2b-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }
        
        .b2b-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .b2b-modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #18181b;
          margin: 0;
        }
        
        .b2b-modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #71717a;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }
        
        .b2b-modal-close:hover {
          color: #18181b;
        }
        
        .b2b-modal-body {
          padding: 24px;
        }
        
        .b2b-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        
        .b2b-form-group {
          margin-bottom: 20px;
        }
        
        .b2b-form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }
        
        .b2b-form-row {
          display: flex;
          gap: 16px;
        }
        
        .b2b-radio-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .b2b-radio-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }
        
        .b2b-radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
          accent-color: #d4a853;
        }
        
        .b2b-export-preview {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: #f0fdf4;
          border-radius: 12px;
          border: 1px solid #bbf7d0;
        }
        
        .b2b-export-preview-icon {
          font-size: 24px;
        }
        
        .b2b-export-preview-text {
          font-size: 13px;
          color: #166534;
          line-height: 1.5;
        }
        
        .b2b-spinner-small {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
